import tokens from "../ds/tokens.json"
import type { TurnView } from "../store"
import { contextParts } from "./format"

/**
 * The link-preview card (Share-v4, "In a WhatsApp thread"): drawn in the browser, where the
 * brand fonts are already loaded, at 1200x630, and uploaded with the share as a JPEG. Always
 * the light theme: a chat preview is a printed thing. Empty string when it cannot be drawn.
 */
export const CARD_W = 1200
export const CARD_H = 630

const color = (name: string): string =>
  (tokens.color.tokens.find((t) => t.name === name)?.value as { light?: string } | undefined)?.light ?? "black"
const fam = tokens.type.families as { sans: string; serif: string; mono: string }

export interface CardText {
  matter: string
  headline: string
  lean: "helps" | "hurts" | "mixed" | null
}

export function cardText(view: TurnView): CardText {
  const head = view.head
  return {
    matter: contextParts(head?.context).join(" · "),
    headline: head?.short || view.question,
    lean: head?.lean?.toward ?? null,
  }
}

/** Lines of `text` that fit `width`, at most `max`, the last ending in an ellipsis if cut. */
export function wrap(measure: (s: string) => number, text: string, width: number, max: number): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean)
  const lines: string[] = []
  let line = ""
  for (let i = 0; i < words.length; i++) {
    const next = line ? `${line} ${words[i]}` : words[i]
    if (measure(next) <= width || !line) {
      line = next
      continue
    }
    lines.push(line)
    line = words[i]
    if (lines.length === max) {
      line = ""
      const last = lines[max - 1]
      let cut = last
      while (cut && measure(`${cut}…`) > width) cut = cut.slice(0, -1).trimEnd()
      lines[max - 1] = `${cut}…`
      return lines
    }
  }
  if (line) lines.push(line)
  return lines
}

function beam(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, tilt: number, bar: string, stroke: number) {
  const k = size / 56
  ctx.save()
  ctx.translate(x, y)
  ctx.scale(k, k)
  ctx.save()
  ctx.translate(28, 26)
  ctx.rotate((tilt * Math.PI) / 180)
  ctx.strokeStyle = bar
  ctx.lineWidth = stroke
  ctx.lineCap = "round"
  ctx.beginPath()
  ctx.moveTo(-20, 0)
  ctx.lineTo(20, 0)
  ctx.stroke()
  ctx.restore()
  ctx.fillStyle = color("ink")
  ctx.beginPath()
  ctx.arc(28, 36, stroke, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

export async function drawShareCard(text: CardText): Promise<string> {
  const canvas = document.createElement("canvas")
  canvas.width = CARD_W
  canvas.height = CARD_H
  const ctx = canvas.getContext("2d")
  if (!ctx) return ""
  try {
    await Promise.all([
      document.fonts?.load(`500 60px ${fam.serif}`),
      document.fonts?.load(`400 26px ${fam.mono}`),
      document.fonts?.load(`600 44px ${fam.sans}`),
    ])
  } catch {
    /* draw with the fallbacks */
  }
  const pad = 72
  ctx.fillStyle = color("ground")
  ctx.fillRect(0, 0, CARD_W, CARD_H)
  ctx.fillStyle = color("for")
  ctx.fillRect(0, 0, CARD_W, 8)

  // The matter, in mono, and the lean glyph opposite it.
  ctx.textBaseline = "alphabetic"
  ctx.font = `400 26px ${fam.mono}`
  ctx.fillStyle = color("ink-3")
  const matter = wrap((s) => ctx.measureText(s).width, text.matter, CARD_W - pad * 2 - 90, 1)[0] ?? ""
  ctx.fillText(matter, pad, pad + 26)
  if (text.lean) {
    const tilt = text.lean === "helps" ? -14 : text.lean === "hurts" ? 14 : 0
    const bar = text.lean === "helps" ? color("for") : text.lean === "hurts" ? color("against") : color("ink-3")
    beam(ctx, CARD_W - pad - 64, pad - 18, 64, tilt, bar, 7)
  }

  // The verdict, in serif, up to three lines.
  ctx.font = `500 64px ${fam.serif}`
  ctx.fillStyle = color("ink")
  const lines = wrap((s) => ctx.measureText(s).width, text.headline, CARD_W - pad * 2, 3)
  const lineH = 78
  const top = 150 + (3 - lines.length) * (lineH / 2)
  lines.forEach((l, i) => ctx.fillText(l, pad, top + lineH * (i + 1) - 14))

  // The foot: a hairline, the lockup, and what this is.
  ctx.fillStyle = color("line")
  ctx.fillRect(pad, CARD_H - 132, CARD_W - pad * 2, 2)
  beam(ctx, pad - 6, CARD_H - 104, 56, -10, color("for"), 4.5)
  ctx.font = `600 46px ${fam.sans}`
  ctx.fillStyle = color("ink")
  ctx.fillText("nira", pad + 60, CARD_H - 52)
  ctx.font = `400 24px ${fam.mono}`
  ctx.fillStyle = color("ink-3")
  const note = "research, not legal advice"
  ctx.fillText(note, CARD_W - pad - ctx.measureText(note).width, CARD_H - 58)

  const url = canvas.toDataURL("image/jpeg", 0.88)
  return url.startsWith("data:image/jpeg;base64,") ? url.slice("data:image/jpeg;base64,".length) : ""
}
